import React from "react"

import { Avatar } from "@coval/ui/avatar"

import type { TranscriptParticipantLabels } from "@/components/transcript/transcriptParticipants"

import {
  AGENT_CHANNEL_INDEX,
  PERSONA_CHANNEL_INDEX,
} from "./useAudioWaveformOptions"

/** The 20px avatar plus the gap between it and the well. */
export const SPEAKER_GUTTER_PIXELS = 32

/** What the two bands are held apart by, so they read as one surface each. */
const LANE_GAP_PIXELS = 2
const WAVEFORM_TOP_PADDING_PIXELS = 12

const LANE_BANDS = [
  { channel: AGENT_CHANNEL_INDEX, className: "bg-coval-chat-agent-surface" },
  { channel: PERSONA_CHANNEL_INDEX, className: "bg-coval-media-track" },
]

/**
 * A band per speaker behind the canvases, each carrying that speaker's chat
 * bubble surface. Each band gives up half the gap on the side facing the split,
 * so the card behind the well shows through as a hairline between them — which
 * is why the well itself draws no background while these are up.
 */
export const SpeakerLaneBands: React.FC<{
  laneHeight: number
  contentHeight?: number
}> = ({ laneHeight, contentHeight = 0 }) => (
  <>
    {LANE_BANDS.map(({ channel, className }) => (
      <div
        key={channel}
        aria-hidden
        className={`absolute inset-x-0 ${className}`}
        style={{
          height: laneHeight - LANE_GAP_PIXELS / 2,
          top:
            channel * (laneHeight + contentHeight) +
            (channel > AGENT_CHANNEL_INDEX ? LANE_GAP_PIXELS / 2 : 0),
        }}
      />
    ))}
  </>
)

/**
 * Faces the two lanes from the gutter to the left of the well, in the channel
 * order the waveform draws them. Decorative: the transcript beside the player
 * already names both speakers. A gutter wider than the avatar alone also names
 * each lane beside its face, truncating to fit.
 */
export const SpeakerLaneAvatars: React.FC<{
  laneHeight: number
  participants: TranscriptParticipantLabels
  width?: number
  contentHeight?: number
}> = ({
  laneHeight,
  participants,
  width = SPEAKER_GUTTER_PIXELS,
  contentHeight = 0,
}) => {
  const showsNames = width > SPEAKER_GUTTER_PIXELS
  const lanes = [
    {
      channel: AGENT_CHANNEL_INDEX,
      name: participants.agentLabel,
      src: participants.agentAvatarUrl,
      tint: participants.agentAvatarTint,
    },
    {
      channel: PERSONA_CHANNEL_INDEX,
      name: participants.userLabel,
      src: participants.userAvatarUrl,
      tint: participants.userAvatarTint,
    },
  ]

  return (
    <div className="flex shrink-0 flex-col" style={{ width }}>
      {lanes.map((lane) => (
        <div
          key={lane.channel}
          className={`flex min-w-0 items-center ${showsNames ? "gap-2 pr-3" : ""}`}
          style={{ height: laneHeight, marginBottom: contentHeight }}
        >
          <Avatar
            size="sm"
            name={lane.name}
            src={lane.src}
            tint={lane.tint}
            decorative
          />
          {showsNames && (
            <span
              className="min-w-0 truncate text-xs text-coval-text-muted"
              title={lane.name}
            >
              {lane.name}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

export function SpeakerChannelLayers({
  childrenByChannel,
  laneHeight,
  contentHeight,
  belowWaveform = false,
}: {
  childrenByChannel?: React.ReactNode[]
  laneHeight: number
  contentHeight: number
  belowWaveform?: boolean
}) {
  return childrenByChannel?.map((children, channel) => (
    <div
      key={channel}
      className={
        belowWaveform
          ? "absolute inset-x-0 z-10"
          : "pointer-events-none absolute inset-x-0"
      }
      style={{
        top:
          channel * (laneHeight + contentHeight) +
          (belowWaveform ? WAVEFORM_TOP_PADDING_PIXELS + laneHeight : 0),
        height: belowWaveform ? contentHeight : laneHeight,
      }}
    >
      {children}
    </div>
  ))
}
